/**
 * Pool Reports Contribution Owner Page
 *
 * Displays vessels for a specific owner with their contribution data.
 * Shows vessel-level breakdown matching Angular implementation.
 */

import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui';
import { useOwnerVessels } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { formatNumber } from '@/utils/formatters';
import { CSVService } from '@/utils/csvExport';
import type { Vessel } from '@/types/poolReports.types';
import {
  POOL_REPORT_VESSEL_COL_HEADER,
  poolReportTheadDrilldownClass,
  poolReportThEmphasisClass,
  poolReportThUnitClass,
  poolReportTdPadClass,
} from '@/components/poolReports/poolReportTableChrome';

export function PoolReportsContributionOwnerPage() {
  const navigate = useNavigate();
  const { ownerId } = useParams<{ ownerId: string }>();
  const [searchParams] = useSearchParams();
  const { reportType, setSubTitle } = usePoolReportsStore();

  const ownerName = searchParams.get('ownerName') || 'Owner';
  const poolId = searchParams.get('poolId');
  const fileId = searchParams.get('fileId');
  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const numericOwnerId = ownerId ? parseInt(ownerId, 10) : 0;

  const {
    data: ownerVessels,
    isLoading,
    isError,
    error: ownerVesselsError,
  } = useOwnerVessels(numericFileId, numericOwnerId, Boolean(fileId && ownerId));

  const vessels = ownerVessels || [];
  const loading = isLoading;
  const error =
    isError && ownerVesselsError
      ? ownerVesselsError instanceof Error
        ? ownerVesselsError.message
        : 'Failed to load vessel data'
      : null;

  // Set subtitle for navigation (hide pool/report filters, show back button)
  useEffect(() => {
    setSubTitle({
      value: `Earnings Contribution and Allocation: ${ownerName}`,
      backLink: `/reports/${reportType}/contribution`,
      queryParams: {
        poolId: poolId || '',
        fileId: fileId || '',
      },
    });

    // Clear subtitle on unmount
    return () => {
      setSubTitle(null);
    };
  }, [ownerName, reportType, poolId, fileId, setSubTitle]);

  useEffect(() => {
    if (!loading && !error && fileId && ownerId && vessels.length === 0) {
      navigate(`/reports/${reportType}/contribution?poolId=${poolId}&fileId=${fileId}`);
    }
  }, [loading, error, fileId, ownerId, vessels.length, navigate, reportType, poolId]);

  // Navigate to vessel details
  function handleVesselClick(vessel: Vessel) {
    if (!vessel.withLink) return;

    navigate(
      `/reports/${reportType}/contribution/owner/${ownerId}/${vessel.vesselId}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(ownerName)}&vesselName=${encodeURIComponent(vessel.name)}`
    );
  }

  // Export to CSV
  function handleExport() {
    if (vessels.length === 0) return;

    const headers = [
      'Vessel Name',
      'DWT (MTS)',
      'Built (Month & Year)',
      'Av Age in TI Fleet (Years)',
      'On-hire Period (Days)',
      'Offhire Period (Days)',
      'Latest Points (Av 100)',
      'Contribution Period to Date (USD)',
      'Contribution RPD (USD)',
      'Allocation Period to Date (USD)',
      'Allocation RPD (USD)',
    ];

    const rows = vessels.map((vessel) => [
      vessel.name || '',
      vessel.dwt !== null ? formatNumber(vessel.dwt, 0) : '',
      vessel.dateBuilt
        ? new Date(vessel.dateBuilt).toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit',
          })
        : '',
      vessel.averageAgeInTIFleet !== null ? formatNumber(vessel.averageAgeInTIFleet, 2) : '',
      vessel.onhirePeriod !== null ? formatNumber(vessel.onhirePeriod, 2) : '',
      vessel.offhirePeriod !== null ? formatNumber(vessel.offhirePeriod, 2) : '',
      vessel.latestPoints !== null ? formatNumber(vessel.latestPoints, 1) : '',
      vessel.contributionAfterParticipantAllocationPeriod !== null
        ? formatNumber(vessel.contributionAfterParticipantAllocationPeriod, 0)
        : '',
      vessel.contributionRPD !== null ? formatNumber(vessel.contributionRPD, 0) : '',
      vessel.allocationInclParticipantAllocationPeriod !== null
        ? formatNumber(vessel.allocationInclParticipantAllocationPeriod, 0)
        : '',
      vessel.allocationInclParticipantAllocationRPD !== null
        ? formatNumber(vessel.allocationInclParticipantAllocationRPD, 0)
        : '',
    ]);

    CSVService.export(rows, `contribution-${ownerName}-vessels`, headers);
  }

  // Render loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
          <div className="h-4 w-64 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
        </div>

        {/* Export button skeleton */}
        <div className="flex justify-end">
          <div className="h-9 w-36 animate-pulse rounded-full bg-gray-200 dark:bg-dark-700"></div>
        </div>

        {/* Table skeleton */}
        <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm dark:border-dark-700">
          <div className="report-table-shell">
            <table className="report-table">
              <thead className={poolReportTheadDrilldownClass}>
                <tr>
                  {Array.from({ length: 11 }).map((_, i) => (
                    <th key={i} className={poolReportThEmphasisClass}>
                      <div className="ml-auto h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 7 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {Array.from({ length: 11 }).map((_, colIndex) => (
                      <td
                        key={colIndex}
                        className="border-t border-gray-200 px-2.5 py-2 dark:border-dark-700 sm:px-3 sm:py-2.5"
                      >
                        <div className="ml-auto h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (vessels.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-dark-700 dark:bg-dark-900">
        <p className="text-gray-500 dark:text-gray-400">No vessel data available for this owner</p>
      </div>
    );
  }

  // Get columns from first vessel
  const columns = [
    { key: 'name', label: POOL_REPORT_VESSEL_COL_HEADER, unit: '' },
    { key: 'dwt', label: 'DWT', unit: 'mts', decimals: 0 },
    { key: 'dateBuilt', label: 'Built', unit: 'Month & Year', isDate: true },
    { key: 'averageAgeInTIFleet', label: 'Av Age in\nTI Fleet', unit: 'Years', decimals: 2 },
    { key: 'onhirePeriod', label: 'On-hire\nPeriod', unit: 'Days', decimals: 2 },
    { key: 'offhirePeriod', label: 'Offhire\nPeriod', unit: 'Days', decimals: 2 },
    { key: 'latestPoints', label: 'Latest\nPoints', unit: 'Av 100', decimals: 1 },
    {
      key: 'contributionAfterParticipantAllocationPeriod',
      label: 'Contribution\nPeriod to Date',
      unit: 'USD',
      decimals: 0,
    },
    {
      key: 'contributionRPD',
      label: 'Contribution\nExpressed as\nRate Per Day',
      unit: 'USD',
      decimals: 0,
    },
    {
      key: 'allocationInclParticipantAllocationPeriod',
      label: 'Allocation\nPeriod to Date',
      unit: 'USD',
      decimals: 0,
    },
    {
      key: 'allocationInclParticipantAllocationRPD',
      label: 'Allocation\nExpressed as\nRate Per Day',
      unit: 'USD',
      decimals: 0,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
        {/* Table Header with Export Button */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-dark-700">
          <h2 className="type-section-title">Vessel Details</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={loading || vessels.length === 0}
            className="rounded-full px-4 shadow-sm"
          >
            Export to Excel
          </Button>
        </div>

        <div className="report-table-shell">
          <table className="report-table">
            <thead className={poolReportTheadDrilldownClass}>
              <tr>
                {columns.map((column, colIndex) => (
                  <th
                    key={column.key}
                    className={clsx(
                      poolReportThEmphasisClass,
                      colIndex === 0 &&
                        'sticky left-0 z-10 min-w-[200px] bg-gray-50 dark:bg-dark-800'
                    )}
                  >
                    <div className="whitespace-pre-line">
                      {column.label}
                      {column.unit && <span className={poolReportThUnitClass}>{column.unit}</span>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vessels.map((vessel, rowIndex) => {
                const isTotal = !vessel.vesselId || vessel.name?.includes('Total');
                const isFirstRow = rowIndex === 0;

                return (
                  <tr
                    key={rowIndex}
                    className={isTotal ? '' : 'hover:bg-gray-50 dark:hover:bg-dark-800'}
                  >
                    {columns.map((column, colIndex) => {
                      const value = vessel[column.key as keyof Vessel];
                      const isVesselColumn = colIndex === 0;

                      return (
                        <td
                          key={column.key}
                          className={clsx(
                            poolReportTdPadClass,
                            'whitespace-nowrap text-gray-900 dark:text-white',
                            isFirstRow && 'border-t-2',
                            isTotal
                              ? 'border-t-2 border-gray-300 bg-gray-50 font-bold dark:border-dark-600 dark:bg-dark-800'
                              : 'border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900',
                            isVesselColumn && 'sticky left-0 z-10 min-w-[200px] font-bold uppercase'
                          )}
                        >
                          {isVesselColumn && value ? (
                            isTotal ? (
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {String(value)}
                              </span>
                            ) : (
                              <div
                                className="flex cursor-pointer items-center justify-end gap-2"
                                onClick={() => handleVesselClick(vessel)}
                              >
                                <span className="text-md font-semibold uppercase text-gray-900 underline hover:no-underline dark:text-white">
                                  {String(value)}
                                </span>
                                <ExternalLink className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400" />
                              </div>
                            )
                          ) : (
                            <span>
                              {column.isDate && value
                                ? new Date(String(value)).toLocaleDateString('en-US', {
                                    month: 'short',
                                    year: '2-digit',
                                  })
                                : typeof value === 'number'
                                  ? formatNumber(value, column.decimals ?? 0, column.decimals ?? 0)
                                  : value || ''}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
